import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, TrendingDown, Target, Sparkles, BarChart2, Award, ChevronRight, ChevronDown, Wallet, Zap, FlaskConical, BookOpen } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import CapitalWidget from '../components/CapitalWidget';
import TraderPerformancePortal from '../components/TraderPerformancePortal';
import JourneyWidget from '../components/JourneyWidget';
import { formatPrice, formatDate, formatNumber } from '../utils/formatters';
import { useSocket } from '../context/SocketContext';
import { cardStyle, cardInnerStyle } from '../utils/cardStyle';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';


function ActQuickWidget({ onNavigate, colors: c, isDark: dark }) {
  const [actData, setActData] = React.useState(null);
  React.useEffect(() => {
    axios.get('/api/learning/act/daily').then(r => setActData(r.data)).catch(() => {});
  }, []);
  const done = actData?.completed === true;
  const accentCol = done ? '#16C784' : c.accent;
  return (
    <div style={{ ...cardStyle(c.cardBg, accentCol, dark), borderRadius: '14px' }}>
      <div className="rounded-[13px] px-3 py-2.5 h-full flex flex-col" style={cardInnerStyle(dark)}>
        <p className="text-[15px] font-bold leading-tight" style={{ color: c.primaryText }}>Your Nyx Coach</p>
        <p className="text-[10px] mt-0.5" style={{ color: c.secondaryText }}>
          Improve your reflex by taking the ACT
        </p>
        <div className="flex-1 flex flex-col justify-end mt-2">
          {done ? (
            <p className="text-[13px] font-bold" style={{ color: '#16C784' }}>Complete 🎉</p>
          ) : (
            <>
              <p className="text-[10px] mb-2" style={{ color: c.secondaryText }}>
                <span style={{ color: '#f59e0b', fontWeight: 600 }}>Pending</span> · just 5 min
              </p>
              <button onClick={onNavigate}
                className="w-full text-[12px] font-bold py-1.5 rounded-md"
                style={{ backgroundColor: accentCol, color: '#fff' }}>
                Take ACT →
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Feature Spotlight Carousel ──────────────────────────────────────────────
const FEATURE_SLIDES = [
  {
    color: '#f59e0b',
    icon: Zap,
    tag: 'Daily ACT',
    title: 'Sharpen your edge.',
    body: 'Your AI coach gives you a quick 5-min behaviour check. Streaks build discipline.',
    path: '/act',
    cta: 'Take Today\'s ACT',
    liveKey: 'act',
  },
  {
    color: '#6366f1',
    icon: BookOpen,
    tag: 'Learning Path',
    title: 'Level up your skills.',
    body: '13 structured levels — charts, risk, F&O, discipline. Learn by doing, track progress.',
    path: '/learning',
    cta: 'Continue Learning',
  },
  {
    color: '#10b981',
    icon: FlaskConical,
    tag: 'Backtesting',
    title: 'Test before you trade.',
    body: 'Run your strategy on real historical data. Know what works before capital is at risk.',
    path: '/backtest',
    cta: 'Run a Backtest',
  },
  {
    color: '#8b5cf6',
    icon: TrendingUp,
    tag: 'Global Cues',
    title: 'Know before markets open.',
    body: 'FII/DII flows, SGX Nifty, US & Asia futures — everything that moves Indian markets.',
    path: '/global-cues',
    cta: 'Check Global Cues',
  },
  {
    color: '#ef4444',
    icon: BarChart2,
    tag: 'F&O Desk',
    title: 'Trade options & futures.',
    body: 'CE/PE buys, writing, futures — with greeks, sizing, and risk controls built in.',
    path: '/derivatives',
    cta: 'Open F&O Desk',
  },
  {
    color: '#0ea5e9',
    icon: TrendingDown,
    tag: 'Market Regime',
    title: 'Know when to trade big.',
    body: 'Scores how often pre-market signals aligned with price action. High-alignment days = trade full size.',
    path: '/market-analysis',
    cta: 'View Regime Analysis',
  },
];

const SLIDE_DURATION = 7000;

function SimBanner({ navigate }) {
  const [active, setActive] = React.useState(0);
  const [dragOffset, setDragOffset] = React.useState(0);
  const [actDone, setActDone] = React.useState(null);
  const isDragging = React.useRef(false);
  const touchStartX = React.useRef(null);
  const timerRef = React.useRef(null);

  React.useEffect(() => {
    if (!document.getElementById('feat-kf')) {
      const s = document.createElement('style');
      s.id = 'feat-kf';
      s.textContent = `
        @keyframes featProg { from { width: 0% } to { width: 100% } }
        @keyframes featGlow { 0%,100% { opacity:.08 } 50% { opacity:.18 } }
        @keyframes featFloat { 0%,100% { transform: translateY(0px) rotate(-8deg); } 50% { transform: translateY(-9px) rotate(-5deg); } }
      `;
      document.head.appendChild(s);
    }
  }, []);

  React.useEffect(() => {
    axios.get('/api/learning/act/daily')
      .then(r => setActDone(r.data?.completed === true))
      .catch(() => setActDone(false));
  }, []);

  const startTimer = React.useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setActive(i => (i + 1) % FEATURE_SLIDES.length);
    }, SLIDE_DURATION);
  }, []);

  React.useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, [startTimer]);

  const goTo = React.useCallback((i) => {
    setActive(i);
    startTimer();
  }, [startTimer]);

  const onTouchStart = React.useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    isDragging.current = true;
  }, []);

  const onTouchMove = React.useCallback((e) => {
    if (touchStartX.current === null) return;
    const diff = e.touches[0].clientX - touchStartX.current;
    setDragOffset(Math.max(-110, Math.min(110, diff)));
  }, []);

  const onTouchEnd = React.useCallback((e) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    isDragging.current = false;
    setDragOffset(0);
    if (Math.abs(diff) > 45) {
      const next = diff < 0
        ? (active + 1) % FEATURE_SLIDES.length
        : (active - 1 + FEATURE_SLIDES.length) % FEATURE_SLIDES.length;
      setActive(next);
      startTimer();
    }
  }, [active, startTimer]);

  const slide = FEATURE_SLIDES[active];

  return (
    <div
      style={{
        borderRadius: 20, overflow: 'hidden', position: 'relative',
        border: `1px solid ${slide.color}35`,
        background: `linear-gradient(135deg, ${slide.color}18 0%, ${slide.color}07 100%)`,
        transition: 'border-color 0.6s ease, background 0.6s ease',
        userSelect: 'none',
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Animated glow blob */}
      <div style={{
        position: 'absolute', top: -55, right: -55,
        width: 220, height: 220, borderRadius: '50%',
        background: slide.color, opacity: 0.11,
        filter: 'blur(75px)',
        animation: 'featGlow 5s ease-in-out infinite',
        transition: 'background 0.6s ease',
        pointerEvents: 'none',
      }} />

      {/* Slides track — real drag-follow */}
      <div style={{
        display: 'flex',
        transform: `translateX(calc(${-active * 100}% + ${dragOffset}px))`,
        transition: isDragging.current ? 'none' : 'transform 0.46s cubic-bezier(0.22, 1, 0.36, 1)',
        willChange: 'transform',
      }}>
        {FEATURE_SLIDES.map((s, i) => {
          const SlideIcon = s.icon;
          const isActSlide = s.liveKey === 'act';
          const actBadge = isActSlide && actDone !== null
            ? (actDone ? { label: 'Done ✓', color: '#16C784' } : { label: 'Pending', color: '#f59e0b' })
            : null;
          const ctaText = isActSlide && actDone === true ? 'Review Score' : s.cta;

          return (
            <div key={i} style={{ minWidth: '100%', padding: '22px 20px 6px', boxSizing: 'border-box', position: 'relative', overflow: 'hidden' }}>
              {/* Floating watermark icon */}
              <div style={{
                position: 'absolute', bottom: 22, right: 14,
                opacity: 0.055,
                animation: 'featFloat 7s ease-in-out infinite',
                animationDelay: `${i * 0.5}s`,
                pointerEvents: 'none',
              }}>
                <SlideIcon size={100} color={s.color} strokeWidth={1.5} />
              </div>

              {/* Tag row */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 13, flexShrink: 0,
                  background: `${s.color}22`, border: `1.5px solid ${s.color}45`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <SlideIcon size={18} color={s.color} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.11em', color: s.color, lineHeight: 1.1 }}>
                    {s.tag}
                  </p>
                  {actBadge && (
                    <p style={{ fontSize: 9, fontWeight: 700, color: actBadge.color, letterSpacing: '0.04em', marginTop: 2 }}>
                      {actBadge.label}
                    </p>
                  )}
                </div>
                <ChevronRight size={13} color={`${s.color}55`} style={{ marginLeft: 'auto', flexShrink: 0 }} />
              </div>

              {/* Title */}
              <p style={{ fontSize: 26, fontWeight: 900, color: '#eceef5', letterSpacing: '-0.03em', lineHeight: 1.1, marginBottom: 9 }}>
                {s.title}
              </p>

              {/* Body */}
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.46)', lineHeight: 1.65, marginBottom: 18 }}>
                {s.body}
              </p>

              {/* CTA */}
              <button
                onClick={(e) => { e.stopPropagation(); navigate(s.path); }}
                style={{
                  padding: '9px 16px',
                  borderRadius: 10,
                  background: `linear-gradient(135deg, ${s.color}CC 0%, ${s.color} 100%)`,
                  border: 'none', cursor: 'pointer',
                  fontSize: 12, fontWeight: 800, color: '#fff',
                  letterSpacing: '0.02em',
                  boxShadow: `0 4px 18px ${s.color}45`,
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {ctaText}
                <ChevronRight size={13} strokeWidth={3} />
              </button>
            </div>
          );
        })}
      </div>

      {/* Tappable progress segments */}
      <div style={{ display: 'flex', gap: 4, padding: '14px 20px 16px' }}>
        {FEATURE_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            style={{
              flex: 1, height: 3, padding: 0, border: 'none', cursor: 'pointer',
              borderRadius: 2, background: `${slide.color}20`, overflow: 'hidden',
            }}
          >
            {i < active && (
              <div style={{ height: '100%', width: '100%', background: slide.color, opacity: 0.5 }} />
            )}
            {i === active && (
              <div
                key={`prog-${active}`}
                style={{
                  height: '100%', background: slide.color,
                  animation: `featProg ${SLIDE_DURATION}ms linear forwards`,
                }}
              />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

function HomePage() {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { user } = useAuth();
  const { prices: socketPrices, onOrderFilled, offOrderFilled, onPositionExited, offPositionExited, onBalanceUpdated, offBalanceUpdated, onReconnect } = useSocket();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState([]);
  const [scannerResults, setScannerResults] = useState([]);
  const [tradingStats, setTradingStats] = useState({ totalTrades: 0, winRate: 0, avgProfit: 0, avgLoss: 0 });
  const [loading, setLoading] = useState(true);
  const [effectiveDate, setEffectiveDate] = useState(null);
  const [marketStatus, setMarketStatus] = useState('closed');
  const [indices, setIndices] = useState([]);
  const [indexFlash, setIndexFlash] = useState({});
  const [activeIndexSlide, setActiveIndexSlide] = useState(0);
  const [indexDropdownOpen, setIndexDropdownOpen] = useState(false);
  const swipeTouchStart = React.useRef(null);
  const prevIndexPrices = React.useRef({});

  useEffect(() => {
    fetchData(); fetchIndices();
  }, []); // eslint-disable-line

  // Re-fetch portfolio only when order/balance socket events fire
  useEffect(() => {
    const refetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/trade/portfolio`);
        setPortfolio(res.data || []);
      } catch (e) { /* silent */ }
    };
    onOrderFilled(refetch);
    onPositionExited(refetch);
    onBalanceUpdated(refetch);
    onReconnect(refetch);
    return () => {
      offOrderFilled(refetch);
      offPositionExited(refetch);
      offBalanceUpdated(refetch);
    };
  }, []); // eslint-disable-line

  // Update index prices from socket when sim is active; flash on change
  useEffect(() => {
    ['NIFTY50', 'BANKNIFTY'].forEach(sym => {
      const live = socketPrices[sym];
      if (!live) return;
      const prev = prevIndexPrices.current[sym];
      if (prev !== undefined && live.price !== prev) {
        const dir = live.price > prev ? 'up' : 'down';
        setIndexFlash(f => ({ ...f, [sym]: dir }));
        setTimeout(() => setIndexFlash(f => ({ ...f, [sym]: null })), 300);
      }
      prevIndexPrices.current[sym] = live.price;
      setIndices(prev => prev.map(idx =>
        idx.symbol === sym ? { ...idx, price: live.price, change_pct: live.change_pct } : idx
      ));
    });
  }, [socketPrices]); // eslint-disable-line

  const fetchIndices = async () => {
    try {
      const [indicesRes, scannerDateRes] = await Promise.all([
        axios.get(`${API_URL}/market/indices`),
        axios.get(`${API_URL}/scanners/last-date`).catch(() => null),
      ]);
      setIndices(indicesRes.data || []);
      if (scannerDateRes?.data?.as_of) setEffectiveDate(scannerDateRes.data.as_of);
      if (scannerDateRes?.data?.market_status) setMarketStatus(scannerDateRes.data.market_status);
    } catch (e) { /* silent */ }
  };

  const fetchData = async () => {
    try {
      const [scannersRes, portfolioRes, tradesRes] = await Promise.allSettled([
        axios.get(`${API_URL}/scanners/results`),
        axios.get(`${API_URL}/trade/portfolio`),
        axios.get(`${API_URL}/trade/history`),
      ]);

      if (scannersRes.status === 'fulfilled') setScannerResults(scannersRes.value.data.slice(0, 4));
      if (portfolioRes.status === 'fulfilled') setPortfolio(portfolioRes.value.data);

      const allTrades = tradesRes.status === 'fulfilled' ? (tradesRes.value.data || []) : [];
      const completed = allTrades.filter(t => t.pnl != null);
      const winning = completed.filter(t => t.pnl > 0);
      const losing = completed.filter(t => t.pnl < 0);
      setTradingStats({
        totalTrades: completed.length,
        winRate: completed.length > 0 ? Math.round((winning.length / completed.length) * 100) : 0,
        avgProfit: winning.length > 0 ? winning.reduce((s, t) => s + t.pnl, 0) / winning.length : 0,
        avgLoss: losing.length > 0 ? losing.reduce((s, t) => s + t.pnl, 0) / losing.length : 0,
      });
    } catch (error) {
      console.error('Failed to fetch data', error);
    } finally {
      setLoading(false);
    }
  };


  const totalInvested = portfolio.reduce((sum, item) => sum + item.total_invested, 0);
  const totalValue = portfolio.reduce((sum, item) => sum + item.current_value, 0);
  const totalPnL = totalValue - totalInvested;

  // Reusable stat tile for Analytics / Algo Flows widgets
  const StatTile = ({ value, label, color, tileColor }) => (
    <div
      className="flex flex-col items-center justify-center py-2 px-2 rounded-md"
      style={{ background: `linear-gradient(135deg, ${tileColor || colors.accent}18, ${tileColor || colors.accent}08)`, border: `1px solid ${tileColor || colors.accent}28` }}
    >
      <p className="text-[15px] font-bold tabular-nums leading-none mb-0.5" style={{ color }}>{value}</p>
      <p className="text-[11px]" style={{ color: colors.secondaryText }}>{label}</p>
    </div>
  );

  // Reusable gradient border card wrapper
  const GlowCard = ({ accentColor, children, onClick, testId, style = {}, innerStyle = {} }) => (
    <div
      style={{ ...cardStyle(colors.cardBg, accentColor, isDark), cursor: onClick ? 'pointer' : 'default', ...style }}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="rounded-2xl overflow-hidden" style={{ ...cardInnerStyle(isDark), ...innerStyle }}>
        {children}
      </div>
    </div>
  );

  const CardHeader = ({ icon: Icon, iconColor, label, right }) => (
    <div
      className="flex items-center justify-between px-3 pt-2.5 pb-2 rounded-t-2xl"
      style={{ backgroundColor: colors.cardHeaderBg, borderBottom: `1px solid ${colors.cardHeaderBorder}` }}
    >
      <div className="flex items-center gap-2">
        {Icon && (
          <div
            className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${iconColor}18` }}
          >
            <Icon className="w-3.5 h-3.5" style={{ color: iconColor }} />
          </div>
        )}
        <span className="text-[13px] font-semibold tracking-wider uppercase" style={{ color: colors.secondaryText }}>{label}</span>
      </div>
      {right}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="home-page">

      {/* Row 1: Welcome + Nifty 50 + Bank Nifty 3-card grid */}
      {(() => {
        const StatusBadge = () => {
          const cfg = marketStatus === 'live-s'
            ? { label: 'LIVE-S', color: '#8b5cf6', pulse: true }
            : marketStatus === 'holiday'
            ? { label: 'Holiday', color: '#f59e0b', pulse: false }
            : marketStatus === 'closed'
            ? { label: 'Closed', color: '#6b7280', pulse: false }
            : { label: 'Weekend', color: '#6b7280', pulse: false };
          return (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 9, fontWeight: 700, letterSpacing: '0.05em',
              padding: '3px 8px', borderRadius: 20,
              background: `${cfg.color}18`, border: `1px solid ${cfg.color}35`, color: cfg.color,
            }}>
              <span style={{
                width: 5, height: 5, borderRadius: '50%', background: cfg.color, flexShrink: 0,
                ...(cfg.pulse ? { animation: 'pulse 1.5s ease-in-out infinite' } : {}),
              }} />
              {cfg.label}
            </span>
          );
        };

const IndexCol = ({ sym, label, routeSym }) => {
          const row      = (indices.find(i => i.symbol === sym) || {});
          const live     = socketPrices[sym];
          const price    = live?.price ?? row.price;
          const chg      = live?.change_pct ?? row.change_pct;
          const chgColor = chg == null ? colors.secondaryText : chg >= 0 ? colors.profit : colors.loss;
          const flashBg  = indexFlash[sym] === 'up' ? 'rgba(22,199,132,0.18)' : indexFlash[sym] === 'down' ? 'rgba(239,68,68,0.18)' : 'transparent';
          return (
            <div className="flex-1 min-w-0 cursor-pointer rounded px-1 -mx-1 transition-colors duration-150"
              style={{ backgroundColor: flashBg }}
              onClick={() => navigate(`/stock/${routeSym}`)}>
              <p className="text-[10px] truncate mb-0.5" style={{ color: colors.secondaryText }}>{label}</p>
              {price != null ? (
                <>
                  <p className="text-[14px] font-bold tabular-nums truncate leading-tight" style={{ color: colors.primaryText }}>{formatNumber(price, 2)}</p>
                  {chg != null && <p className="text-[10px] font-semibold leading-tight" style={{ color: chgColor }}>{chg >= 0 ? '+' : ''}{chg.toFixed(2)}%</p>}
                </>
              ) : <p className="text-[14px] font-bold" style={{ color: colors.secondaryText }}>—</p>}
            </div>
          );
        };

        const SLIDES = [
          { sym: 'NIFTY50',   label: 'Nifty 50',   routeSym: '^NSEI' },
          { sym: 'BANKNIFTY', label: 'Bank Nifty',  routeSym: '^NSEBANK' },
        ];
        const active = SLIDES[activeIndexSlide];

        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {/* Left: Welcome — tapping opens the app info sheet */}
            <div
              onClick={() => window.dispatchEvent(new Event('open-app-info'))}
              style={{
                display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                textAlign: 'left', padding: '10px 12px',
                borderRadius: 16,
                background: `linear-gradient(145deg, ${colors.accent}1a 0%, ${colors.accent}08 60%, transparent 100%)`,
                border: `1px solid ${colors.accent}35`,
                position: 'relative', overflow: 'hidden',
                cursor: 'pointer',
              }}
            >
              <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: `radial-gradient(circle, ${colors.accent}18 0%, transparent 70%)`, pointerEvents: 'none' }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <p style={{ fontSize: 14, fontWeight: 800, color: colors.primaryText, margin: 0, letterSpacing: '-0.01em' }}>
                  Welcome, {user?.full_name?.split(' ')[0] || 'Trader'}
                </p>
                <p style={{ fontSize: 10, color: colors.secondaryText, margin: 0, lineHeight: 1.4 }}>Today: {formatDate(new Date().toISOString())}</p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {effectiveDate
                    ? <p style={{ fontSize: 10, color: colors.secondaryText, margin: 0, lineHeight: 1.4 }}>Sim: {formatDate(effectiveDate)}</p>
                    : <span />}
                  <ChevronRight size={14} style={{ color: `${colors.accent}80`, flexShrink: 0 }} />
                </div>
              </div>
            </div>

            {/* Right: index card */}
            <div style={{
                position: 'relative', borderRadius: 16,
                background: `linear-gradient(145deg, ${colors.accent}14 0%, ${colors.accent}06 60%, transparent 100%)`,
                border: `1px solid ${colors.accent}30`,
              }}>

              {/* ── One index at a time + dropdown selector ── */}
              <div style={{ display: 'flex', flexDirection: 'column', padding: '10px 12px', gap: 2 }}>
                {/* Top row: label + badge */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: colors.secondaryText }}>{active.label}</span>
                  <StatusBadge />
                </div>

                {/* Price + chevron inline at bottom */}
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', cursor: 'pointer' }} onClick={() => navigate(`/stock/${active.routeSym}`)}>
                  <IndexCol sym={active.sym} label="" routeSym={active.routeSym} />
                  <button
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 7, backgroundColor: `${colors.accent}15`, border: `1px solid ${colors.accent}25`, cursor: 'pointer', flexShrink: 0, marginLeft: 6 }}
                    onClick={e => { e.stopPropagation(); setIndexDropdownOpen(o => !o); }}>
                    <ChevronDown style={{ width: 11, height: 11, color: colors.accent }} />
                  </button>
                </div>
              </div>

              {/* Dropdown — outside the flex column so it floats above as a true overlay */}
              {indexDropdownOpen && (
                <>
                  <div style={{
                    position: 'absolute', top: 8, right: 8, zIndex: 20,
                    borderRadius: 10, overflow: 'hidden',
                    backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`,
                    minWidth: 140, boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  }}>
                    {SLIDES.map((s, i) => (
                      <button key={s.sym}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          width: '100%', padding: '10px 12px',
                          backgroundColor: i === activeIndexSlide ? `${colors.accent}15` : 'transparent',
                          borderBottom: i === 0 ? `1px solid ${colors.border}` : 'none',
                          cursor: 'pointer',
                        }}
                        onClick={() => { setActiveIndexSlide(i); setIndexDropdownOpen(false); }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: i === activeIndexSlide ? colors.accent : colors.primaryText }}>{s.label}</span>
                        {i === activeIndexSlide && <span style={{ fontSize: 10, color: colors.accent }}>✓</span>}
                      </button>
                    ))}
                    <button
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        width: '100%', padding: '8px 12px',
                        borderTop: `1px solid ${colors.border}`, cursor: 'pointer',
                      }}
                      onClick={() => { navigate(`/stock/${active.routeSym}`); setIndexDropdownOpen(false); }}>
                      <span style={{ fontSize: 10, color: colors.secondaryText }}>View detail page</span>
                      <ChevronRight style={{ width: 12, height: 12, color: colors.secondaryText }} />
                    </button>
                  </div>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 10 }} onClick={() => setIndexDropdownOpen(false)} />
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* Journey + Global Cues — feature cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {[
          { path: '/appguide',    Icon: Target,    color: colors.accent,  label: 'App Guide',    sub: 'How every feature works' },
          { path: '/global-cues', Icon: TrendingUp, color: '#8b5cf6',     label: 'Global Cues',  sub: 'Pre-market intel & FII flows' },
        ].map(({ path, Icon, color, label, sub }) => (
          <button key={path} onClick={() => navigate(path)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
              padding: '14px 13px', borderRadius: 16, textAlign: 'left', cursor: 'pointer',
              background: `linear-gradient(145deg, ${color}16 0%, ${color}06 100%)`,
              border: `1px solid ${color}32`,
              position: 'relative', overflow: 'hidden',
            }}>
            <div style={{ position: 'absolute', top: -18, right: -18, width: 70, height: 70, borderRadius: '50%', background: `radial-gradient(circle, ${color}18 0%, transparent 70%)`, pointerEvents: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', marginBottom: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: 11, background: `${color}22`, border: `1px solid ${color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon style={{ width: 16, height: 16, color }} />
              </div>
              <ChevronRight style={{ width: 14, height: 14, color: `${color}80` }} />
            </div>
            <p style={{ fontSize: 13, fontWeight: 800, color, margin: 0, letterSpacing: '-0.01em' }}>{label}</p>
            <p style={{ fontSize: 10, color: colors.secondaryText, margin: '3px 0 0', lineHeight: 1.4 }}>{sub}</p>
          </button>
        ))}
      </div>

      {/* Journey nudge */}
      <button
        onClick={() => navigate('/journey')}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 14px', borderRadius: 16, textAlign: 'left', cursor: 'pointer',
          background: `linear-gradient(90deg, ${colors.accent}16 0%, ${colors.accent}06 100%)`,
          border: `1px solid ${colors.accent}32`,
          position: 'relative', overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 80, background: `linear-gradient(90deg, transparent, ${colors.accent}08)`, pointerEvents: 'none' }} />
        <div style={{ width: 32, height: 32, borderRadius: 10, background: `${colors.accent}20`, border: `1px solid ${colors.accent}38`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Target style={{ width: 15, height: 15, color: colors.accent }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: 13, fontWeight: 800, color: colors.accent, margin: 0, letterSpacing: '-0.01em' }}>Your Journey</p>
          <p style={{ fontSize: 10, color: colors.secondaryText, margin: '2px 0 0' }}>research → trade → review · daily flow</p>
        </div>
        <ChevronRight style={{ width: 14, height: 14, flexShrink: 0, color: `${colors.accent}70` }} />
      </button>

      {/* Sim mode info banner */}
      <SimBanner navigate={navigate} />

      {/* Full-width Capital Control */}
      <CapitalWidget compact virtualBalance={user?.virtual_balance} />

      {/* Trader Performance Portal */}
      <TraderPerformancePortal />


      {/* Portfolio + Trade Analytics */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Portfolio — summary + holdings merged */}
          <GlowCard accentColor={totalPnL >= 0 ? '#10b981' : '#ef4444'} onClick={() => navigate('/orders')}>
            <CardHeader icon={Wallet} iconColor={totalPnL >= 0 ? '#10b981' : '#ef4444'} label="Portfolio" right={<ChevronRight className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />} />
            <div className="grid grid-cols-2 gap-1.5 px-3 pt-2 pb-2">
              {[
                { label: 'Total Value', value: formatPrice(totalValue, 0),                                          color: colors.primaryText },
                { label: 'Invested',    value: formatPrice(totalInvested, 0),                                       color: colors.primaryText },
                { label: 'P&L',         value: `${totalPnL >= 0 ? '+' : '-'}${formatPrice(Math.abs(totalPnL), 0)}`, color: totalPnL >= 0 ? colors.profit : colors.loss },
                { label: 'Holdings',    value: portfolio.length,                                                    color: colors.primaryText },
              ].map(({ label, value, color }) => (
                <div key={label} className="flex flex-col items-center justify-center py-2 px-1 rounded-md"
                  style={{ background: totalPnL >= 0 ? 'linear-gradient(135deg, #10b98118, #10b98108)' : 'linear-gradient(135deg, #ef444418, #ef444408)', border: `1px solid ${totalPnL >= 0 ? '#10b98128' : '#ef444428'}` }}>
                  <p className="text-[14px] font-bold tabular-nums leading-none mb-0.5" style={{ color }}>{value}</p>
                  <p className="text-[10px]" style={{ color: colors.secondaryText }}>{label}</p>
                </div>
              ))}
            </div>
            {portfolio.length > 0 && (
              <div className="px-2 pb-2 space-y-1">
                {portfolio.slice(0, 3).map((holding) => {
                  const hColor = holding.unrealized_pnl >= 0 ? colors.profit : colors.loss;
                  return (
                    <div
                      key={holding.symbol}
                      className="flex items-center justify-between px-2 py-1.5 rounded-lg cursor-pointer transition-all"
                      style={{ background: 'linear-gradient(135deg, #10b98114, #10b98106)', border: '1px solid #10b98122' }}
                      onClick={() => navigate(`/stock/${holding.symbol.replace('.NS', '')}`)}
                      onMouseEnter={e => e.currentTarget.style.background = 'linear-gradient(135deg, #10b98122, #10b98110)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'linear-gradient(135deg, #10b98114, #10b98106)'}
                    >
                      <p className="text-[12px] font-bold truncate" style={{ color: colors.primaryText }}>{holding.symbol.replace('.NS', '')}</p>
                      <p className="text-[12px] font-semibold flex-shrink-0 ml-1" style={{ color: hColor }}>
                        {holding.pnl_percentage != null ? `${holding.unrealized_pnl >= 0 ? '+' : ''}${holding.pnl_percentage.toFixed(2)}%` : '—'}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </GlowCard>

          <GlowCard accentColor="#3b82f6" onClick={() => navigate('/trading-analytics')} testId="trading-analytics-widget">
            <CardHeader icon={BarChart2} iconColor="#3b82f6" label="Trade Analytics" right={<ChevronRight className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />} />
            <div className="grid grid-cols-2 gap-2 px-3 py-2.5">
              <StatTile value={tradingStats.totalTrades}                       label="Trades"     color={colors.primaryText} tileColor="#3b82f6" />
              <StatTile value={`${tradingStats.winRate}%`}                     label="Win Rate"   color={colors.profit}      tileColor="#3b82f6" />
              <StatTile value={formatPrice(tradingStats.avgProfit, 0)}         label="Avg Profit" color={colors.profit}      tileColor="#3b82f6" />
              <StatTile value={formatPrice(Math.abs(tradingStats.avgLoss), 0)} label="Avg Loss"   color={colors.loss}        tileColor="#3b82f6" />
            </div>
          </GlowCard>
        </div>


    </div>
  );
}

export default HomePage;
